// S41 AI 使用記錄(AI usage log)資料層。
//
// 集中稽核平台各 consumer(LLM 金鑰消費端)實際發生的 LLM 呼叫。**兩端點**(restful
// `feat/assistant-key-config` / 使用記錄分支同時在建,契約 v4):
//   GET /v3/admin/ai-usage       — 摘要列表(**無完整內容**),query consumer/actor/from/to/limit/cursor
//   GET /v3/admin/ai-usage/{id}  — 單筆明細(**含完整 request + response 內容**)
//
// 兩端點皆 `CanAdminRead`,走 restful 標準 `{data}` 信封 → 直接用 lib/api 的 `apiFetch`
// (它會抽 `body.data`、處理 401 refresh、把 403 轉成 ErrorState「無權限」)。列表分頁採
// opaque cursor(`next_cursor` "" 或省略皆代表末頁,normalizeCursor 收斂)。時間戳=**微秒**
// epoch(FACTS PACK §2;用 lib/format 的 formatTimestampUs)。

import { apiFetch, normalizeCursor } from '../../lib/api';

const AI_USAGE_PREFIX = '/v3/admin/ai-usage';

/** 列表一筆(摘要;**永不含完整 request/response**,只帶 preview)。 */
export interface AiUsageRow {
  /** 穩定記錄 id(明細查詢用)。 */
  id: string;
  /** 發起呼叫的 consumer(金鑰消費端),如 `admin` / `dev-portal-assistant`。 */
  consumer: string;
  /** 觸發者 user id(後端欄名 actor_user_id;**精確比對**,不是關鍵字搜尋)。 */
  actor: string;
  /** 觸發者角色(後端 actor_role;缺席即空字串)。 */
  actor_role: string;
  /** 使用模型。 */
  model: string;
  /** 輸入 token 數(缺省 null)。 */
  input_tokens: number | null;
  /** 輸出 token 數(缺省 null)。 */
  output_tokens: number | null;
  /** 往返延遲毫秒(缺省 null)。 */
  latency_ms: number | null;
  /** 呼叫是否成功。 */
  ok: boolean;
  /** 發生時間(微秒 epoch;缺省 null)。 */
  ts: number | null;
  /** 內容摘要片段(後端已裁切;非完整內容)。 */
  preview: string;
}

/** 明細(摘要欄 + **完整** request / response 內容;供分析改善)。 */
export interface AiUsageDetail extends AiUsageRow {
  /** 完整請求內容(字串或已序列化的 JSON)。 */
  request: string;
  /** 完整回覆內容(字串或已序列化的 JSON)。 */
  response: string;
  /** 保留天數(隱私提示用;缺省 null)。 */
  retention_days: number | null;
}

/** 把任意內容欄位正規化為可顯示字串(物件 → 縮排 JSON)。 */
function toText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/**
 * 由明細的 expire_at_s(epoch 秒)與該筆的時間戳(µs)推回保留天數。
 * 兩者缺一或算出非正值就回 null —— 寧可不顯示,也不要顯示一個猜的數字。
 */
function retentionDaysFrom(expireAtS: number | null, tsUs: number | null): number | null {
  if (expireAtS === null || tsUs === null) return null;
  const days = Math.round((expireAtS - tsUs / 1_000_000) / 86_400);
  return days > 0 ? days : null;
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

/**
 * 把 `<input type="date">` 給的 `YYYY-MM-DD` 轉成後端收得下的 RFC3339。
 *
 * `/v3/admin/ai-usage` 屬**嚴格時間族**(handler `parseTimeParam`):只收 RFC3339
 * 或 epoch 微秒,`YYYY-MM-DD` 一律 400 —— 也就是說使用者只要挑一個日期,整個
 * 請求就失敗。`edge='end'` 取當日 23:59:59.999,讓結束日**包含**當天(使用者選
 * 7/12 的意思是「到 7/12 為止」,不是「到 7/12 零點」)。
 * 已是 RFC3339 或純數字(epoch)的值原樣放行。
 */
export function toApiTime(v: string | undefined, edge: 'start' | 'end'): string | undefined {
  const raw = (v ?? '').trim();
  if (raw === '') return undefined;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return edge === 'start' ? `${raw}T00:00:00.000Z` : `${raw}T23:59:59.999Z`;
}

/** 受檢投影一列摘要:只讀已知非機密欄位。 */
export function parseAiUsageRow(raw: unknown): AiUsageRow {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: typeof r.id === 'string' ? r.id : String(r.id ?? ''),
    consumer: typeof r.consumer === 'string' ? r.consumer : '',
    // 後端欄名是 actor_user_id / ts_us / request_preview(handler/admin_ai_usage.go
    // usageSummaryJSON)。本投影原本讀 actor / ts / preview —— 那是照一份從未存在的
    // v4 契約寫的,所以整頁的 actor、時間、預覽三欄恆為空。保留舊鍵作為回退,
    // 讓任何仍在送舊形狀的環境不會突然變空白。
    actor: typeof r.actor_user_id === 'string' ? r.actor_user_id : typeof r.actor === 'string' ? r.actor : '',
    actor_role: typeof r.actor_role === 'string' ? r.actor_role : '',
    model: typeof r.model === 'string' ? r.model : '',
    input_tokens: toNum(r.input_tokens),
    output_tokens: toNum(r.output_tokens),
    latency_ms: toNum(r.latency_ms),
    ok: Boolean(r.ok),
    ts: toNum(r.ts_us) ?? toNum(r.ts),
    preview: typeof r.request_preview === 'string' ? r.request_preview : typeof r.preview === 'string' ? r.preview : '',
  };
}

/** 受檢投影明細:摘要欄 + 完整 request/response 內容。 */
export function parseAiUsageDetail(raw: unknown): AiUsageDetail {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    ...parseAiUsageRow(r),
    request: toText(r.request),
    response: toText(r.response),
    // ⚠️ 後端**從不回** retention_days(保留天數只在 env RA_AI_USAGE_RETENTION_DAYS)。
    // 由明細的 expire_at_s(epoch 秒)自行換算,換不出來就留 null 讓 UI 走通用文案 ——
    // 隱私提示寧可含糊,也不要顯示一個編出來的天數。
    retention_days: retentionDaysFrom(toNum(r.expire_at_s), toNum(r.ts_us) ?? toNum(r.ts)),
  };
}

export interface AiUsageFilter {
  consumer?: string;
  actor?: string;
  /** 起始日期。可給 `YYYY-MM-DD`(送出前轉成 RFC3339 當日 00:00Z)或直接給 RFC3339。 */
  from?: string;
  /** 結束日期。`YYYY-MM-DD` 會轉成當日 23:59:59.999Z(**包含**當天)。 */
  to?: string;
  limit?: number;
  cursor?: string;
}

/** 一頁列表結果(含下一頁 cursor 與保留天數提示)。 */
export interface AiUsagePage {
  rows: AiUsageRow[];
  /** null = 無更多頁(涵蓋 "" 與缺欄兩種)。 */
  nextCursor: string | null;
  /** 保留天數(隱私提示;後端於列表 data 帶回時填入)。 */
  retentionDays: number | null;
  requestId: string | null;
}

/**
 * GET /v3/admin/ai-usage → 一頁摘要列表。容忍列表放在 `items` / `usage` / `records`,
 * 或 data 本身即為陣列;cursor 讀 `next_cursor`(opaque)。
 */
export async function fetchAiUsage(f: AiUsageFilter = {}, signal?: AbortSignal): Promise<AiUsagePage> {
  const res = await apiFetch<unknown>(AI_USAGE_PREFIX, {
    query: { consumer: f.consumer, actor: f.actor, from: toApiTime(f.from, 'start'), to: toApiTime(f.to, 'end'), limit: f.limit, cursor: f.cursor },
    signal,
  });
  const data = res.data;
  const obj = data && !Array.isArray(data) ? (data as Record<string, unknown>) : {};
  const rawList: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray(obj.items)
      ? (obj.items as unknown[])
      : Array.isArray(obj.usage)
        ? (obj.usage as unknown[])
        : Array.isArray(obj.records)
          ? (obj.records as unknown[])
          : [];
  return {
    rows: rawList.map(parseAiUsageRow),
    nextCursor: normalizeCursor(typeof obj.next_cursor === 'string' ? obj.next_cursor : null),
    // ⚠️ 列表端點**不回**保留天數,這裡永遠是 null。保留欄位是為了讓 UI 的隱私
    // 提示有統一出口 —— 真正的天數只能從明細的 expire_at_s 換算(見 parseAiUsageDetail)。
    // 先前這裡讀 obj.retention_days,是照一份不存在的契約寫的。
    retentionDays: null,
    requestId: res.requestId,
  };
}

/** GET /v3/admin/ai-usage/{id} → 單筆明細(含完整 request/response)。 */
export async function fetchAiUsageDetail(id: string, signal?: AbortSignal): Promise<AiUsageDetail> {
  const res = await apiFetch<unknown>(`${AI_USAGE_PREFIX}/${encodeURIComponent(id)}`, { signal });
  return parseAiUsageDetail(res.data);
}

// ── 顯示輔助 ──────────────────────────────────────────────────────────────────

/** tokens 摘要:「輸入 / 輸出」;皆缺省時回「—」。 */
export function formatTokens(input: number | null, output: number | null): string {
  if (input === null && output === null) return '—';
  return `${input ?? '—'} / ${output ?? '—'}`;
}

/** 延遲毫秒 → 可讀(<1s 用 ms,否則秒)。 */
export function formatUsageLatency(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}
